import React, { useState, useEffect, useLayoutEffect } from "react";
import { getLanguageNativeName } from "byzantine/src/l10n/Languages";
import { ReactLocalization } from "@fluent/react";
import { LANG_CHANGE, LANG_ERROR } from "../browserCustomEvents";
import { getAllBundles, getReactLocalization } from "./util.js";
import { DEBUG_STYLES } from "./debugStyles";
import { getSelectedLocale } from "byzantine/src/l10n/InstanceHelpers";

const setLocaleStorage = (locale: string): void => {
  try {
    window.localStorage.setItem("selectedLocale", locale);
  } catch (error) {
    if (typeof window !== "undefined" && window.Sentry) {
      window.Sentry?.captureException(error as Error);
    }
  }
  // Set cookie for backend to read on initial page load
  try {
    if (typeof document !== "undefined") {
      document.cookie = `lang=${encodeURIComponent(locale)}; path=/`;
    }
  } catch (error) {
    if (typeof window !== "undefined" && window.Sentry) {
      window.Sentry?.captureException(error as Error);
    }
  }
};

const getStoredLocale = (): string | null => {
  try {
    return window.localStorage.getItem("selectedLocale");
  } catch (error) {
    if (typeof window !== "undefined" && window.Sentry) {
      window.Sentry?.captureException(error as Error);
    }
    return null;
  }
};

declare global {
  interface Window {
    isFetchingFtls: boolean;
    Sentry?: {
      captureException(error: Error): void;
      captureMessage(
        message: string,
        captureContext: {
          level: string;
          extra: {
            [x: string]: unknown;
          };
          tags: {
            [x: string]: number | string | boolean | bigint | symbol | null | undefined;
          };
        }
      ): string;
    };
  }
}

/**
 * We only fetch FTLs on the client, so on initial render
 * OR server render, make the parser in the ReactLocalization
 * return a single text node for `Localized` contents
 *
 * @param {String} htmlFragment
 * @returns {Array} NodeList with single text node
 */
export const ssrMarkupParser = (htmlFragment: string) => [
  {
    nodeName: "#text",
    textContent: htmlFragment.toUpperCase(),
  },
];

/**
 * Convert a locale code to a native name
 * @param {String} localeCode
 */
const toNativeName = (localeCode: string) => {
  const nativeName = getLanguageNativeName(localeCode);
  if (nativeName.length < 1) {
    return "DEBUG MODE";
  }
  return nativeName;
};

// @see <https://github.com/narmi/banking/blob/70a1167f8cc6f83118fd533bc2b9f39568b78512/indigo/settings/utils.py#L10>
const isUat = (institutionInternalName: string) => {
  const normalizedName = `${institutionInternalName}`.toLowerCase();
  return normalizedName.endsWith("uat") || normalizedName.endsWith("local");
};

/**
 * Allow deep linking to any page with a language already selected
 * (e.g. narmiurl.com?lang=es)
 */
const getLangParam = () => {
  let langFromParams;
  if (typeof window !== "undefined") {
    const langValue = new URL(document.location as unknown as string).searchParams.get("lang");
    if (langValue) {
      langFromParams = langValue;
    }
  }
  return langFromParams;
};

const getLocaleDetailsFromBundlesMap = (bundlesMap: Record<string, any>) => {
  const isUserSelectableFilter = (key: string) =>
    !key.includes("-settings") && !key.includes("-default");
  const codes = Object.keys(bundlesMap).filter(isUserSelectableFilter);
  const names = Object.keys(bundlesMap).filter(isUserSelectableFilter).map(toNativeName);
  return { codes, names };
};

interface FluentLocalizationOptions {
  internalName: string | undefined;
  s3ImagesBucket: string | undefined;
  productCode: "fi" | "cao" | "bao" | "sao" | "app" | "olb" | "lending";
  apiUrl: string;
  isMfe?: boolean;
}
/**
 * Hook that returns a `ReactLocalization` that can be passed as the `l10n`
 * prop to a Fluent `LocalizationProvider`.
 *
 * This hook also returns the necessary information and setter for building
 * a language selection UI within the consuming application.
 *
 * When `setSelectedLocale` is called with a new locale code, the `l10n` value
 * returned from this hook will update.
 *
 * @usage
 * ```
 * const {
 *    l10n, // pass this to your `LocalizationProvider`
 *    isLoading,
 *    isError,
 *    isMultiLingual,
 *    localeCodes,
 *    localeNames,
 *    selectedLocale,
 *    setSelectedLocale,
 * } = useFluentLocalization({
 *    internalName: "cathay_UAT",
 *    productCode: "olb",
 *    apiUrl: `${API_URL_ENV_VAR}/v1/`,
 *    isMfe: false, // microfrontend flag (azul)
 * })
 * ```
 */
const useFluentLocalization = ({
  internalName,
  s3ImagesBucket,
  productCode,
  apiUrl,
  isMfe = false,
}: FluentLocalizationOptions) => {
  const [isLoading, setIsLoading] = useState(true); // until success or error
  const [isError, setIsError] = useState(false);
  const [l10n, setL10n] = useState(new ReactLocalization([], ssrMarkupParser as any));
  const [bundlesMap, setBundlesMap] = useState(null);
  const [selectedLocale, setSelectedLocale] = useState(getLangParam());
  const [localeCodes, setLocaleCodes] = useState(["en"]);
  const [localeNames, setLocaleNames] = useState(["English"]);
  const showDebugOption = isUat(internalName as string);

  // This effect ensures `bundlesMap` is only populated on client side render.
  //
  // The effect is run again if `selectedLocale` changes, updating the `l10n`
  // property returned by this hook.
  useEffect(() => {
    async function populateBundlesMap() {
      const bundleData = await getAllBundles(
        internalName as string,
        s3ImagesBucket,
        productCode,
        apiUrl,
        showDebugOption
      );
      if (isMfe) {
        window.isFetchingFtls = false;
      }
      // @ts-expect-error
      if (typeof bundleData.error !== "undefined") {
        if (isMfe) {
          window.dispatchEvent(new CustomEvent(LANG_ERROR));
        }
        setIsError(true);
        setIsLoading(false);
        // @ts-expect-error For the benefit of Sentry tracing
        console.error(bundleData.error);
      } else {
        // @ts-expect-error Unaddressed type error on `bundles` from fluent
        setBundlesMap(bundleData.bundles);
        setIsError(false);
        setIsLoading(false);
      }
    }

    try {
      const storedLocale = getStoredLocale();
      // Persist user language selection across sessions and pages
      if (!selectedLocale && storedLocale) {
        setSelectedLocale(storedLocale);
      } else if (selectedLocale) {
        // if selectedLocale changes via user action, add it to localStorage and set cookie
        setLocaleStorage(selectedLocale);
      }
    } catch {
      // an error occurred accessing local storage
    }

    // handle microfrontend case if applicable; prevent multiple
    // react roots from fetching the same FTLs
    const isAlreadyFetching = isMfe ? window.isFetchingFtls : false;

    // consumers of this hook typically need to fetch to get `internalName`,
    // so we must wait until we have a value before populating bundles
    if (!bundlesMap && !isAlreadyFetching && internalName) {
      if (isMfe) {
        window.isFetchingFtls = true;
      }
      populateBundlesMap();
    }

    if (bundlesMap) {
      const { codes, names } = getLocaleDetailsFromBundlesMap(bundlesMap);
      const reactLocalization = getReactLocalization(selectedLocale as string, bundlesMap);
      setL10n(reactLocalization);
      setLocaleCodes(codes);
      setLocaleNames(names);
    }
  }, [internalName, selectedLocale, bundlesMap]);

  useEffect(() => {
    if (!isMfe) {
      window.document.documentElement.lang = selectedLocale || "en";
    }
  }, [selectedLocale]);

  // Microfrontend CustomEvent pub/sub
  // Allows a `ReactLocalization` to be shared between multiple React roots
  useEffect(() => {
    if (isMfe) {
      const handleLangChange = ({ detail }: CustomEvent<Record<"l10n" | "bundlesMap", any>>) => {
        const { l10n, bundlesMap } = detail;
        const { codes, names } = getLocaleDetailsFromBundlesMap(bundlesMap);
        if (typeof l10n?.areBundlesEmpty === "function" && !l10n.areBundlesEmpty()) {
          setL10n(l10n);
          setLocaleCodes(codes);
          setLocaleNames(names);
          setIsLoading(false);
          window.document.documentElement.lang = getSelectedLocale(l10n);
        }
      };

      const handleLangError = () => {
        setIsLoading(false);
      };

      window.addEventListener(LANG_CHANGE, handleLangChange as EventListener);
      window.addEventListener(LANG_ERROR, handleLangError);

      return () => {
        window.removeEventListener(LANG_CHANGE, handleLangChange as EventListener);
        window.removeEventListener(LANG_ERROR, handleLangError);
      };
    }
  }, [isMfe]);

  // Debug mode styles
  useLayoutEffect(() => {
    let style: any;
    if (selectedLocale === "debug") {
      style = document.createElement("style");
      style.textContent = DEBUG_STYLES;
      document.head.appendChild(style);
    }
    return () => (style ? style.remove() : null);
  }, [selectedLocale]);

  const isMultilingual = localeCodes.length > 1;

  return {
    l10n,
    isError,
    isLoading,
    isMultilingual,
    localeCodes,
    localeNames,
    selectedLocale,
    setSelectedLocale,
  };
};

export default useFluentLocalization;
